<script setup lang="ts">
import { RouterLink } from "vue-router";
import { computed, ref, watch } from "vue";
import Button from "./Button.vue";
import Modal from "./Modal.vue";
import SearchField from "./SearchField.vue";
import StatusChip from "./StatusChip.vue";
import TemplatePreview from "./TemplatePreview.vue";
import { TEMPLATE_HEADER_MEDIA } from "@desfecha/contracts";
import { adminApi, type TemplateRow } from "../api/admin";
import { errorText } from "../api/errors";
import { toast } from "../composables/feedback";
import { uploadFileTo } from "../composables/inbox";
import {
  pickerTemplates,
  templateBody,
  templateChip,
  templateStatusLabel,
} from "../composables/templateStatus";
import { locale, t, type MessageKey } from "../i18n";
import { dayLabel, type ActiveMessages } from "../api/activeMessages";
const props = defineProps<{
  open: boolean;
  slug: string;
  busy?: boolean;
  contactName?: string;
  /** The number's WhatsApp account: a template goes out only through a number of its account. */
  accountId?: string | null;
  /** The contact's active messages (decision 0036): marketing waits while paused or refused. */
  active?: ActiveMessages | null;
}>();
const emit = defineEmits<{
  close: [];
  send: [
    payload: {
      templateId: string;
      parameters: Record<string, unknown>;
      preview: string;
      /** A template with a media header: the file staged for this send. */
      headerUploadId?: string;
    },
  ];
}>();
const templates = ref<TemplateRow[]>([]);
const loading = ref(false);
const query = ref("");
const selected = ref<TemplateRow | null>(null);
const values = ref<Record<string, string>>({});
interface Component {
  type: string;
  format?: string;
  text?: string;
  buttons?: Array<{ type: string; text: string; url?: string }>;
}
const components = computed(
  () => (selected.value?.components ?? []) as Component[],
);
const bodyText = computed(
  () =>
    components.value.find((c) => c.type.toUpperCase() === "BODY")?.text ?? "",
);
const headerText = computed(() => {
  const h = components.value.find((c) => c.type.toUpperCase() === "HEADER");
  return h && (h.format ?? "TEXT").toUpperCase() === "TEXT"
    ? (h.text ?? "")
    : "";
});
/** A photo, video or document header: each send brings its own file. */
const headerFormat = computed(() => {
  const h = components.value.find((c) => c.type.toUpperCase() === "HEADER");
  const f = (h?.format ?? "").toLowerCase();
  return f === "image" || f === "video" || f === "document" ? f : null;
});
const headerFile = ref<{ id: string; url: string | null; name: string } | null>(
  null,
);
const uploadingHeader = ref(false);
const headerInput = ref<HTMLInputElement>();
async function chooseHeader(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = "";
  const format = headerFormat.value;
  if (!file || !format) return;
  uploadingHeader.value = true;
  try {
    const up = await uploadFileTo(
      props.slug,
      file,
      format === "document" ? "document" : "media",
    );
    headerFile.value = {
      id: up.id,
      url: format === "image" ? URL.createObjectURL(file) : null,
      name: file.name,
    };
  } catch (err) {
    toast(errorText(err));
  } finally {
    uploadingHeader.value = false;
  }
}
const footerText = computed(
  () =>
    components.value.find((c) => c.type.toUpperCase() === "FOOTER")?.text ?? "",
);
const buttons = computed(
  () =>
    components.value.find((c) => c.type.toUpperCase() === "BUTTONS")?.buttons ??
    [],
);
const placeholders = (text: string) =>
  Array.from(
    new Set(Array.from(text.matchAll(/\{\{(\w+)\}\}/g)).map((m) => m[1]!)),
  );
const bodyParams = computed(() => placeholders(bodyText.value));
const headerParams = computed(() => placeholders(headerText.value));
/** Link buttons whose address ends in a variable: the ending is filled per message. */
const urlParams = computed(() =>
  buttons.value
    .map((b, index) => ({ b, index }))
    .filter(
      ({ b }) =>
        b.type.toUpperCase() === "URL" && /\{\{\w+\}\}/.test(b.url ?? ""),
    ),
);
/** Named parameters ({{first_name}}) must say their name to Meta; positional ones do not. */
const param = (k: string, text: string) =>
  /^\d+$/.test(k)
    ? { type: "text", text }
    : { type: "text", parameter_name: k, text };
const fill = (text: string, prefix: string) =>
  text.replace(
    /\{\{(\w+)\}\}/g,
    (_, k: string) => values.value[`${prefix}:${k}`] || `{{${k}}}`,
  );
const preview = computed(() =>
  [fill(headerText.value, "h"), fill(bodyText.value, "b"), footerText.value]
    .filter(Boolean)
    .join("\n\n"),
);
const complete = computed(
  () =>
    (!headerFormat.value || Boolean(headerFile.value)) &&
    bodyParams.value.every((k) => (values.value[`b:${k}`] ?? "").trim()) &&
    headerParams.value.every((k) => (values.value[`h:${k}`] ?? "").trim()) &&
    urlParams.value.every(({ index }) =>
      (values.value[`u:${index}`] ?? "").trim(),
    ),
);
const bodyOf = templateBody;
const picked = computed(() =>
  pickerTemplates(templates.value, props.accountId, query.value),
);
/**
 * Held for this contact (decision 0036): marketing while paused or refused, and utility when the
 * company holds it. Utility with "ask" stays here; the send asks before it goes.
 */
const held = (tpl: TemplateRow) => {
  const a = props.active;
  if (!a || a.status === "can_receive") return false;
  const category = tpl.category.toLowerCase();
  return (
    category === "marketing" || (category === "utility" && a.utility === "hold")
  );
};
const filtered = computed(() =>
  picked.value.approved.filter((tpl) => !held(tpl)),
);
const heldList = computed(() => picked.value.approved.filter(held));
const waiting = computed(() => picked.value.waiting);
const activeNotice = computed(() => {
  const a = props.active;
  if (!a || a.status === "can_receive") return "";
  return a.status === "paused" && a.until
    ? t("active.picker.paused", {
        name: props.contactName ?? "",
        day: dayLabel(a.until, locale.value),
      })
    : t("active.picker.opted_out", { name: props.contactName ?? "" });
});
/** The WhatsApp accounts behind the company's numbers: none means no number is connected yet. */
const accountIds = ref<string[]>([]);
async function load() {
  loading.value = true;
  try {
    const [tp, ch] = await Promise.all([
      adminApi.templates(props.slug),
      adminApi.channels(props.slug).catch(() => ({ channels: [] })),
    ]);
    templates.value = tp.templates;
    accountIds.value = [
      ...new Set(
        ch.channels
          .map((c) => c.account_id)
          .filter((id): id is string => Boolean(id)),
      ),
    ].filter((id) => !props.accountId || id === props.accountId);
  } finally {
    loading.value = false;
  }
}
/** Brings the account's templates from Meta now (approvals also arrive by themselves). */
const syncing = ref(false);
async function syncNow() {
  syncing.value = true;
  try {
    let synced = 0;
    for (const id of accountIds.value)
      synced += (await adminApi.syncTemplates(props.slug, id)).synced;
    await load();
    toast(
      synced
        ? t("template.synced", { count: synced })
        : t("template.synced_none"),
    );
  } catch (err) {
    toast(errorText(err));
  } finally {
    syncing.value = false;
  }
}
function pick(tpl: TemplateRow) {
  selected.value = tpl;
  headerFile.value = null;
  const next: Record<string, string> = {};
  const first = props.contactName?.split(" ")[0] ?? "";
  for (const k of placeholders(
    (tpl.components as Component[]).find((c) => c.type.toUpperCase() === "BODY")
      ?.text ?? "",
  ))
    next[`b:${k}`] = k === "1" && first ? first : "";
  values.value = next;
}
function send() {
  if (!selected.value) return;
  const comps: unknown[] = [];
  if (headerParams.value.length)
    comps.push({
      type: "header",
      parameters: headerParams.value.map((k) =>
        param(k, values.value[`h:${k}`] ?? ""),
      ),
    });
  if (bodyParams.value.length)
    comps.push({
      type: "body",
      parameters: bodyParams.value.map((k) =>
        param(k, values.value[`b:${k}`] ?? ""),
      ),
    });
  for (const { index } of urlParams.value)
    comps.push({
      type: "button",
      sub_type: "url",
      index: String(index),
      parameters: [{ type: "text", text: values.value[`u:${index}`] ?? "" }],
    });
  emit("send", {
    templateId: selected.value.id,
    parameters: comps.length ? { components: comps } : {},
    preview: preview.value,
    ...(headerFile.value ? { headerUploadId: headerFile.value.id } : {}),
  });
}
watch(
  () => props.open,
  (open) => {
    if (open) {
      selected.value = null;
      query.value = "";
      void load();
    }
  },
);
</script>
<template>
  <Modal :open="open" :title="t('template.pick')" wide @close="emit('close')">
    <div v-if="!selected" class="template-picker">
      <SearchField v-model="query" :placeholder="t('template.search')" />
      <p v-if="!loading && activeNotice" class="template-active-notice small">
        {{ activeNotice }}
      </p>
      <p v-if="loading" class="muted small">{{ t("inbox.loading") }}</p>
      <div v-else-if="!accountIds.length" class="template-empty">
        <p class="muted small">{{ t("template.no_number") }}</p>
        <RouterLink
          :to="'/settings/numbers'"
          class="wa-button secondary compact"
          @click="emit('close')"
          >{{ t("template.connect_number") }}</RouterLink
        >
      </div>
      <div
        v-else-if="!filtered.length && !heldList.length"
        class="template-empty"
      >
        <p class="muted small">{{ t("template.none_approved") }}</p>
        <div class="row-actions">
          <Button
            variant="secondary"
            compact
            icon="refresh"
            :loading="syncing"
            @click="syncNow"
            >{{ t("template.sync_now") }}</Button
          >
          <RouterLink
            :to="'/settings/templates'"
            class="wa-button ghost compact"
            @click="emit('close')"
            >{{ t("template.create") }}</RouterLink
          >
        </div>
      </div>
      <button
        v-for="tpl in filtered"
        :key="tpl.id"
        class="template-row"
        @click="pick(tpl)"
      >
        <span class="grow"
          ><strong>{{ tpl.name }}</strong>
          <small class="muted"
            >· {{ tpl.language }} ·
            {{
              t(("template.category." + tpl.category.toLowerCase()) as never)
            }}</small
          ><br /><small class="template-body">{{ bodyOf(tpl) }}</small></span
        >
      </button>
      <template v-if="!loading && heldList.length">
        <h4 class="template-group">{{ t("active.picker.held") }}</h4>
        <div
          v-for="tpl in heldList"
          :key="tpl.id"
          class="template-row disabled"
          aria-disabled="true"
        >
          <span class="grow"
            ><strong>{{ tpl.name }}</strong>
            <small class="muted"
              >· {{ tpl.language }} ·
              {{
                t(("template.category." + tpl.category.toLowerCase()) as never)
              }}</small
            ><br /><small class="template-body">{{ bodyOf(tpl) }}</small></span
          >
          <StatusChip
            status="pending"
            :label="
              active?.status === 'paused'
                ? t('active.status.paused')
                : t('active.status.opted_out')
            "
          />
        </div>
      </template>
      <template v-if="!loading && waiting.length">
        <h4 class="template-group">{{ t("template.waiting") }}</h4>
        <div
          v-for="tpl in waiting"
          :key="tpl.id"
          class="template-row disabled"
          aria-disabled="true"
        >
          <span class="grow"
            ><strong>{{ tpl.name }}</strong>
            <small class="muted"
              >· {{ tpl.language }} ·
              {{
                t(("template.category." + tpl.category.toLowerCase()) as never)
              }}</small
            ><br /><small class="template-body">{{ bodyOf(tpl) }}</small></span
          >
          <StatusChip
            :status="templateChip(tpl.status)"
            :label="templateStatusLabel(tpl.status)"
          />
        </div>
      </template>
    </div>
    <div v-else class="template-compose">
      <button class="link-button" @click="selected = null">
        ← {{ t("template.change") }}
      </button>
      <div v-if="headerFormat" class="tpl-sample">
        <span class="grow small"
          ><strong>{{ headerFile?.name ?? t("template.header_file") }}</strong
          ><br /><small class="muted">{{
            t("template.header_file_hint", {
              kind: t(
                `templates.header.${headerFormat}` as MessageKey,
              ).toLowerCase(),
            })
          }}</small></span
        >
        <input
          ref="headerInput"
          type="file"
          hidden
          :accept="TEMPLATE_HEADER_MEDIA[headerFormat].mimes.join(',')"
          @change="chooseHeader"
        />
        <Button
          variant="secondary"
          compact
          icon="media"
          :loading="uploadingHeader"
          @click="headerInput?.click()"
          >{{ t("template.header_file_choose") }}</Button
        >
      </div>
      <div
        v-if="headerParams.length || bodyParams.length || urlParams.length"
        class="modal-form"
      >
        <label v-for="k in headerParams" :key="'h' + k" class="wa-field"
          ><span
            >{{ t("template.param", { n: k }) }} ·
            {{ t("template.header") }}</span
          ><input v-model="values[`h:${k}`]" maxlength="1024"
        /></label>
        <label v-for="k in bodyParams" :key="'b' + k" class="wa-field"
          ><span>{{ t("template.param", { n: k }) }}</span
          ><input v-model="values[`b:${k}`]" maxlength="1024"
        /></label>
        <label
          v-for="{ b, index } in urlParams"
          :key="'u' + index"
          class="wa-field"
          ><span>{{ t("template.param_button", { text: b.text }) }}</span
          ><input v-model="values[`u:${index}`]" maxlength="2000"
        /></label>
      </div>
      <p v-else class="muted small">{{ t("template.no_params") }}</p>
      <div class="template-preview">
        <span class="sg-demo-label">{{ t("template.preview") }}</span>
        <TemplatePreview
          :header-media="
            headerFormat
              ? {
                  format: headerFormat,
                  url: headerFile?.url ?? null,
                  name: headerFile?.name ?? null,
                }
              : null
          "
          :header="fill(headerText, 'h')"
          :body="fill(bodyText, 'b')"
          :footer="footerText"
          :buttons="buttons"
        />
      </div>
    </div>
    <template #footer>
      <Button variant="secondary" @click="emit('close')">{{
        t("common.cancel")
      }}</Button>
      <Button
        v-if="selected"
        icon="send"
        :loading="busy"
        :disabled="!complete"
        @click="send"
        >{{ t("template.send") }}</Button
      >
    </template>
  </Modal>
</template>
